import React, { useState, useEffect, useCallback } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  FaChartLine,
  FaCalendarAlt,
  FaShoppingBag,
  FaRupeeSign,
  FaEnvelope,
  FaBolt,
  FaSyncAlt,
  FaExclamationCircle
} from "react-icons/fa";
import { fetchWithAuth } from "../../../utils/api";
import "./TopmateBookings.css";

// Register ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function TopmateAnalytics() {
  const [dateRange, setDateRange] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analytics, setAnalytics] = useState({
    summary: {
      totalBookingsCount: 0,
      totalPurchasesCount: 0,
      totalRevenue: 0,
      sessionRevenue: 0,
      productRevenue: 0
    },
    serviceDistribution: {},
    productDistribution: {},
    emailStats: {},
    webhookStats: {},
    timeline: []
  });

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetchWithAuth(`/admin/topmate/analytics?dateRange=${dateRange}`);
      const data = await res.json();

      if (data.success) {
        setAnalytics(data.analytics);
      } else {
        setError(data.message || "Failed to load analytics");
      }
    } catch (err) {
      setError("Network error fetching analytics");
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Timeline Chart Data
  const timelineLabels = analytics.timeline.map((t) => {
    // e.g. "20261029" -> "29 Oct"
    if (t.date && t.date.length === 8) {
      const year = t.date.substring(0, 4);
      const month = t.date.substring(4, 6);
      const day = t.date.substring(6, 8);
      const d = new Date(`${year}-${month}-${day}T00:00:00.000+05:30`);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
    return t.date;
  });

  const activityLineData = {
    labels: timelineLabels.length > 0 ? timelineLabels : ["No Data"],
    datasets: [
      {
        label: "Session Bookings",
        data: analytics.timeline.map((t) => t.bookings),
        borderColor: "#38bdf8",
        backgroundColor: "rgba(56, 189, 248, 0.15)",
        fill: true,
        tension: 0.35
      },
      {
        label: "Digital Purchases",
        data: analytics.timeline.map((t) => t.purchases),
        borderColor: "#c084fc",
        backgroundColor: "rgba(192, 132, 252, 0.15)",
        fill: true,
        tension: 0.35
      }
    ]
  };

  // Service Distribution Doughnut Data
  const serviceKeys = Object.keys(analytics.serviceDistribution || {});
  const serviceValues = Object.values(analytics.serviceDistribution || {});
  const serviceDoughnutData = {
    labels: serviceKeys.length > 0 ? serviceKeys : ["None Recorded"],
    datasets: [
      {
        data: serviceValues.length > 0 ? serviceValues : [1],
        backgroundColor: ["#38bdf8", "#0284c7", "#34d399", "#f59e0b", "#ec4899", "#8b5cf6"],
        borderColor: "#0f172a",
        borderWidth: 2
      }
    ]
  };

  // Email Stats Bar Data
  const emailLabels = ["Sent", "Failed", "Pending"];
  const emailBarData = {
    labels: emailLabels,
    datasets: [
      {
        label: "Emails Dispatched",
        data: [
          analytics.emailStats?.Sent || 0,
          analytics.emailStats?.Failed || 0,
          analytics.emailStats?.Pending || 0
        ],
        backgroundColor: ["rgba(52, 211, 153, 0.7)", "rgba(248, 113, 113, 0.7)", "rgba(251, 191, 36, 0.7)"],
        borderRadius: 8
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: "#94a3b8",
          font: { size: 12 }
        }
      }
    },
    scales: {
      x: {
        grid: { color: "rgba(255, 255, 255, 0.05)" },
        ticks: { color: "#64748b" }
      },
      y: {
        grid: { color: "rgba(255, 255, 255, 0.05)" },
        ticks: { color: "#64748b", stepSize: 1 }
      }
    }
  };

  return (
    <div className="topmate-dashboard-container">
      {/* HEADER */}
      <div className="topmate-header-card">
        <div className="topmate-header-text">
          <div className="topmate-header-badge">
            <FaChartLine /> Topmate Performance Insights
          </div>
          <h1>Topmate Analytics</h1>
          <p>
            Aggregated metrics based strictly on actual customer bookings, verified revenue, popular session topics, and
            system dispatch health.
          </p>
        </div>

        <div className="topmate-header-actions">
          <select
            className="topmate-select"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="7d">Last 7 Days (IST)</option>
            <option value="30d">Last 30 Days (IST)</option>
            <option value="90d">Last 90 Days (IST)</option>
            <option value="year">This Year (IST)</option>
            <option value="all">All Time (IST)</option>
          </select>

          <button className="topmate-btn-secondary" onClick={fetchAnalytics} disabled={loading}>
            <FaSyncAlt className={loading ? "spin-icon" : ""} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="topmate-alert danger">
          <FaExclamationCircle /> <span>{error}</span>
        </div>
      )}

      {/* SUMMARY KPI ROW */}
      <div className="topmate-kpi-grid">
        <div className="topmate-kpi-card" style={{ cursor: "default" }}>
          <div className="kpi-card-header">
            <span className="kpi-title">Selected Period Bookings</span>
            <div className="kpi-icon-wrap cyan">
              <FaCalendarAlt />
            </div>
          </div>
          <div className="kpi-value">{loading ? "..." : analytics.summary?.totalBookingsCount}</div>
          <div className="kpi-hint">Session calls booked in range</div>
        </div>

        <div className="topmate-kpi-card" style={{ cursor: "default" }}>
          <div className="kpi-card-header">
            <span className="kpi-title">Selected Period Purchases</span>
            <div className="kpi-icon-wrap purple">
              <FaShoppingBag />
            </div>
          </div>
          <div className="kpi-value">{loading ? "..." : analytics.summary?.totalPurchasesCount}</div>
          <div className="kpi-hint">Digital downloads ordered</div>
        </div>

        <div className="topmate-kpi-card" style={{ cursor: "default" }}>
          <div className="kpi-card-header">
            <span className="kpi-title">Total Verified Revenue</span>
            <div className="kpi-icon-wrap emerald">
              <FaRupeeSign />
            </div>
          </div>
          <div className="kpi-value">
            {loading ? "..." : `₹${(analytics.summary?.totalRevenue || 0).toLocaleString("en-IN")}`}
          </div>
          <div className="kpi-hint">
            Sessions: ₹{analytics.summary?.sessionRevenue || 0} | Products: ₹{analytics.summary?.productRevenue || 0}
          </div>
        </div>
      </div>

      {/* CHARTS GRID */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: "20px" }}>
        {/* ACTIVITY OVER TIME */}
        <div className="topmate-card" style={{ minHeight: "350px" }}>
          <div className="card-header-row">
            <div className="card-header-title">
              <FaChartLine style={{ color: "#38bdf8" }} />
              <h3>Activity Trends (IST)</h3>
            </div>
          </div>
          <div style={{ height: "260px" }}>
            <Line data={activityLineData} options={chartOptions} />
          </div>
        </div>

        {/* SERVICES DISTRIBUTION */}
        <div className="topmate-card" style={{ minHeight: "350px" }}>
          <div className="card-header-row">
            <div className="card-header-title">
              <FaCalendarAlt style={{ color: "#34d399" }} />
              <h3>Session Topic Distribution</h3>
            </div>
          </div>
          <div style={{ height: "260px" }}>
            <Doughnut
              data={serviceDoughnutData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: { color: "#94a3b8", font: { size: 11 } }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* EMAIL DISPATCH HEALTH */}
        <div className="topmate-card" style={{ minHeight: "350px" }}>
          <div className="card-header-row">
            <div className="card-header-title">
              <FaEnvelope style={{ color: "#c084fc" }} />
              <h3>Email Delivery Health</h3>
            </div>
          </div>
          <div style={{ height: "260px" }}>
            <Bar data={emailBarData} options={chartOptions} />
          </div>
        </div>

        {/* WEBHOOK DISPATCH HEALTH */}
        <div className="topmate-card" style={{ minHeight: "350px" }}>
          <div className="card-header-row">
            <div className="card-header-title">
              <FaBolt style={{ color: "#f59e0b" }} />
              <h3>Webhook Processing Results</h3>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "20px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: "rgba(0,0,0,0.25)", borderRadius: "10px" }}>
              <span style={{ color: "#34d399" }}>Successful Webhooks (200 OK)</span>
              <strong>{analytics.webhookStats?.Success || 0}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: "rgba(0,0,0,0.25)", borderRadius: "10px" }}>
              <span style={{ color: "#f87171" }}>Failed / Unauthorized Webhooks</span>
              <strong>{analytics.webhookStats?.Failed || 0}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: "rgba(0,0,0,0.25)", borderRadius: "10px" }}>
              <span style={{ color: "#94a3b8" }}>Ignored / Duplicates Handled</span>
              <strong>{analytics.webhookStats?.Ignored || 0}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TopmateAnalytics;
